/**
 * First-login onboarding (medopd-docs 102). The server works out every step from the real data for the signed-in
 * person at their active clinic; the app only shows it and sends skip / confirm / finish.
 */
import { fetchApi } from '../client';

export type OnboardingAudience = 'CLINIC_ADMIN' | 'DOCTOR' | 'LISTING_DOCTOR';
export type OnboardingStepStatus = 'DONE' | 'TODO' | 'WAITING';

export interface OnboardingStepView {
  /** Stable id, e.g. "clinic.hours". Unknown keys still render, as a link to `route`. */
  key: string;
  order: number;
  required: boolean;
  status: OnboardingStepStatus;
  /** Short code the app turns into a sentence (e.g. MISSING_HOURS); null for none. */
  detail: string | null;
  /** Names the sentence lists (doctors without hours, missing field codes). */
  items: string[];
  /** Where the same setting lives outside the wizard, or null. */
  route: string | null;
  confirmable: boolean;
  skipped: boolean;
  confirmed: boolean;
}

export interface OnboardingStatus {
  applicable: boolean;
  audience: OnboardingAudience | null;
  clinicId: string | null;
  clinicName: string | null;
  steps: OnboardingStepView[];
  total: number;
  done: number;
  requiredPending: number;
  recommendedPending: number;
  /** First run not finished and a required step still to do: keep the person in the wizard. */
  blocking: boolean;
  startedAt: string | null;
  completedAt: string | null;
  /** Screens the pending required steps link to; reachable while blocking. */
  allowedPaths: string[];
}

export interface ClinicOnboardingProgress {
  clinicId: string;
  clinicName: string;
  city: string | null;
  createdAt: string | null;
  state: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
  adminName: string | null;
  requiredPending: number;
  recommendedPending: number;
  pendingRequiredKeys: string[];
  startedAt: string | null;
  completedAt: string | null;
}

const step = (key: string) => `/api/onboarding/steps/${encodeURIComponent(key)}`;

export const onboardingModule = {
  status: () => fetchApi<OnboardingStatus>('/api/onboarding'),
  skip: (key: string) => fetchApi<OnboardingStatus>(`${step(key)}/skip`, { method: 'POST' }),
  unskip: (key: string) => fetchApi<OnboardingStatus>(`${step(key)}/skip`, { method: 'DELETE' }),
  confirm: (key: string) => fetchApi<OnboardingStatus>(`${step(key)}/confirm`, { method: 'POST' }),
  complete: () => fetchApi<OnboardingStatus>('/api/onboarding/complete', { method: 'POST' }),
  /** Only the clinic's sole doctor may set their own fee; otherwise the admin does. */
  setOwnConsultation: (body: { opdFee: number; consultationDurationMinutes?: number }) =>
    fetchApi<OnboardingStatus>('/api/onboarding/doctor-consultation', { method: 'PUT', body: JSON.stringify(body) }),
  /** Super admin: setup progress of recent clinics. */
  clinicOverview: () => fetchApi<ClinicOnboardingProgress[]>('/api/admin/onboarding/clinics'),
};
